import React, { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Loader2, Globe, MapPin, X, Plus, Ban, Search, Users, TrendingUp } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/kiwify`;

const PLANOS_DISPONIVEIS = [
  { id: "iniciante", nome: "Iniciante", mercado: "br", cor: "bg-gray-600" },
  { id: "universal", nome: "Universal", mercado: "br", cor: "bg-blue-600" },
  { id: "empresa", nome: "Diamante Brasil", mercado: "br", cor: "bg-purple-600" },
  { id: "internacional", nome: "Internacional", mercado: "internacional", cor: "bg-emerald-600" },
];

const corPlano = (nomePlano) => {
  const encontrado = PLANOS_DISPONIVEIS.find((p) => nomePlano?.toLowerCase().includes(p.nome.toLowerCase()));
  return encontrado?.cor || "bg-gray-600";
};

/**
 * Tela do admin para ver e gerenciar os planos dos usuários - a peça que
 * decide quem tem acesso ao Painel Internacional (plano pago separado do
 * plano Brasil).
 *
 * REDESENHADA (usuário reportou que a versão anterior estava confusa,
 * "sem lógica"): agora separa claramente Brasil de Internacional em
 * abas, com busca por e-mail, e cada plano com selo colorido por tipo -
 * em vez de uma lista plana e uniforme onde tudo parecia igual.
 */
const GestaoPlanos = () => {
  const [carregando, setCarregando] = useState(true);
  const [planos, setPlanos] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState("br");
  const [busca, setBusca] = useState("");
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoEmail, setNovoEmail] = useState("");
  const [novoPlano, setNovoPlano] = useState("universal");

  const carregarPlanos = async () => {
    setCarregando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/planos`);
      if (resp.ok) setPlanos(await resp.json());
    } catch (error) {
      console.error("Erro ao carregar planos:", error);
      toast.error("Não foi possível carregar os planos.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
  }, []);

  const ativarPlano = async () => {
    if (!novoEmail) {
      toast.error("Informe o e-mail do usuário.");
      return;
    }
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(
        `${API}/simular?email=${encodeURIComponent(novoEmail)}&plano=${novoPlano}`,
        { method: "POST" }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao ativar plano");
      toast.success("Plano ativado com sucesso!");
      setModalAberto(false);
      setNovoEmail("");
      await carregarPlanos();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const cancelarPlano = async (email) => {
    if (!window.confirm(`Cancelar o plano de ${email}?`)) return;
    try {
      const resp = await authService.fetchAuth(`${API}/plano/${encodeURIComponent(email)}/cancelar`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao cancelar plano");
      toast.success("Plano cancelado.");
      await carregarPlanos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const { planosBrasil, planosInternacional, totalAtivosBrasil, totalAtivosInternacional } = useMemo(() => {
    const br = planos.filter((p) => p.mercado !== "internacional");
    const intl = planos.filter((p) => p.mercado === "internacional");
    return {
      planosBrasil: br,
      planosInternacional: intl,
      totalAtivosBrasil: br.filter((p) => p.status === "ativo").length,
      totalAtivosInternacional: intl.filter((p) => p.status === "ativo").length,
    };
  }, [planos]);

  const listaExibida = (abaAtiva === "br" ? planosBrasil : planosInternacional).filter(
    (p) => !busca || p.usuario_email?.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CreditCard className="w-7 h-7 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Gestão de Planos</h1>
            <p className="text-sm text-gray-400">Quem tem acesso Brasil e quem tem acesso Internacional</p>
          </div>
        </div>
        <Button onClick={() => setModalAberto(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Ativar Plano Manualmente
        </Button>
      </div>

      {/* Cartões-resumo, clicáveis, servindo também de seletor de aba */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card
          onClick={() => setAbaAtiva("br")}
          className={`p-4 cursor-pointer transition-colors border-2 ${
            abaAtiva === "br" ? "border-orange-500 bg-gray-800" : "border-transparent bg-gray-800/50 hover:bg-gray-800"
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Planos Brasil</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-white">{totalAtivosBrasil}</p>
            <p className="text-xs text-gray-500">ativos de {planosBrasil.length} total</p>
          </div>
        </Card>
        <Card
          onClick={() => setAbaAtiva("internacional")}
          className={`p-4 cursor-pointer transition-colors border-2 ${
            abaAtiva === "internacional" ? "border-blue-500 bg-gray-800" : "border-transparent bg-gray-800/50 hover:bg-gray-800"
          }`}
        >
          <p className="text-xs text-gray-500 flex items-center gap-1"><Globe className="w-3 h-3" /> Planos Internacional</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-blue-400">{totalAtivosInternacional}</p>
            <p className="text-xs text-gray-500">ativos de {planosInternacional.length} total</p>
          </div>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          placeholder="Buscar por e-mail..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-9"
        />
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : listaExibida.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          <Users className="w-10 h-10 mx-auto mb-2 text-gray-600" />
          {busca ? "Nenhum plano encontrado com essa busca." : `Nenhum plano ${abaAtiva === "br" ? "do Brasil" : "internacional"} ainda.`}
        </Card>
      ) : (
        <div className="space-y-2">
          {listaExibida.map((p, idx) => (
            <Card key={idx} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${p.status === "ativo" ? "bg-green-400" : "bg-red-400"}`} />
                <div>
                  <p className="text-white font-medium">{p.usuario_email}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded text-white ${corPlano(p.plano_nome)}`}>
                      {p.plano_nome}
                    </span>
                    <span className={`text-xs ${p.status === "ativo" ? "text-green-400" : "text-red-400"}`}>
                      {p.status}
                    </span>
                  </div>
                </div>
              </div>
              {p.status === "ativo" && (
                <Button size="sm" variant="outline" onClick={() => cancelarPlano(p.usuario_email)}>
                  <Ban className="w-3 h-3 mr-1" />
                  Cancelar
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Ativar Plano Manualmente</h3>
              <button onClick={() => setModalAberto(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-3">
              <Input
                placeholder="E-mail do usuário"
                value={novoEmail}
                onChange={(e) => setNovoEmail(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                {PLANOS_DISPONIVEIS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setNovoPlano(p.id)}
                    className={`p-2 rounded-md text-sm font-medium text-white transition-opacity ${p.cor} ${
                      novoPlano === p.id ? "ring-2 ring-white" : "opacity-60 hover:opacity-90"
                    }`}
                  >
                    {p.nome}
                    <p className="text-[10px] opacity-80">{p.mercado === "internacional" ? "🌐 Internacional" : "🇧🇷 Brasil"}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
              <Button onClick={ativarPlano} disabled={salvando} className="bg-emerald-600 hover:bg-emerald-700">
                {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Ativar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GestaoPlanos;
